import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import { format } from 'date-fns';
import saveAs from 'file-saver';
import get from 'lodash.get';
import moment from 'moment';
import Papa from 'papaparse';
import { Typography, Box, Card, Button, Overlay, Loader, color, Icon } from '@kistech/kis-ui';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box as MuiBox,
  Grid,
  Typography as MuiTypography,
  Chip
} from '@mui/material';
import { Tune } from '@mui/icons-material';
import { ActionOverlay } from 'components/ActionOverlay';
import { useActions } from 'hooks/useActions';
import { selectors as activityLogSelectors, actions as activityLogActions, BaseEvent } from 'store/activityLog';
import { selectors as adminsSelectors, actions as adminsActions } from 'store/admins';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { selectors as kioskSelectors, actions as kioskActions } from 'store/kiosks';
import { useStyles } from './styled';
import { ActivityLogFilterPopUp } from './components/ActivityLogFilterPopUp';

// FORMATS THE ACTIVITYLOG OBJECT FOR EACH COLUMN
const formatActivityLog = (activityLog: BaseEvent[]) => {
  return activityLog.map((r) => ({
    date: r.createdAt ? format(new Date(r.createdAt), 'MM/dd/yyyy hh:mm a') : '',
    user: get(r.actorInfo, 'name'),
    kioskName: r.name,
    action: r.event
  }));
};

const ActivityLogPage: React.FC = () => {
  const classes = useStyles()();
  // ACTIONS AND SELECTORS
  const { getActivityLog, resetActivityLog, resetExportData, getAdmins, getKiosks } = useActions({
    getActivityLog: activityLogActions.getActivityLog,
    resetActivityLog: activityLogActions.resetActivityLog,
    resetExportData: activityLogActions.resetExportData,
    getAdmins: adminsActions.getAdmins,
    getKiosks: kioskActions.getKiosks
  });
  const displayedActivityLog = useSelector(activityLogSelectors.selectActivityLog);
  const activityLogPending = useSelector(activityLogSelectors.selectPending);
  const hasMore = useSelector(activityLogSelectors.selectHasMore);
  const totalRecords = useSelector(activityLogSelectors.selectTotalRecords);
  const exportData = useSelector(activityLogSelectors.selectExportData);
  const exportPending = useSelector(activityLogSelectors.selectExportPending);
  const admins = useSelector(adminsSelectors.selectAdmins);
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const kioskData = useSelector(kioskSelectors.selectKiosks);

  const [showFiltersDrawer, setShowFiltersDrawer] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [sortColumn, setSortColumn] = React.useState('created_at');
  const [sortDirection, setSortDirection] = React.useState('desc');
  const [sortParams, setSortParams] = React.useState({ column: 'created_at', order: 'desc' });
  const [kioskIdsToFilter, setKioskIdsToFilter] = React.useState<string[]>([]);
  const [usersToFilter, setUsersToFilter] = React.useState<string[]>([]);
  const [selectedEndDate, setSelectedEndDate] = React.useState<Date | null>(null);
  const [selectedStartDate, setSelectedStartDate] = React.useState<Date | null>(null);
  const [filterChips, setFilterChips] = React.useState<string[]>([]);
  const [chipClosed, setChipClosed] = React.useState(false);
  const [filter, setFilter] = React.useState(false);
  const [logRefreshedAt, setLogRefreshedAt] = React.useState('');

  // FORMATS ACTIVITYLOG TO FIT HOW THE TABLE SHOULD DISPLAY THE DATA
  const formattedActivityLog = useMemo(() => formatActivityLog(displayedActivityLog), [displayedActivityLog]);

  // ON FETCH: SHOW OVERLAY IF NO DATA
  React.useEffect(() => {
    if (displayedActivityLog.length <= 0 && !activityLogPending) {
      setShowConfirmation(true);
    } else {
      setShowConfirmation(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedActivityLog, activityLogPending]);

  // ON FILTER REMOVAL: FETCH
  React.useEffect(() => {
    if (chipClosed) {
      handleFilter();
      setChipClosed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartDate, selectedEndDate, kioskIdsToFilter, usersToFilter]);

  // ON UPDATE OF CLIENT || SUCCESSFUL: RESET
  React.useEffect(() => {
    // RESET ACTIVITY LOG ON CLIENT CHANGE
    resetActivityLog();
    resetFilters();
    setPage(1);
    // IF CLIENT EXIST OR SUCCESSFUL ACTION - GET ACTIVITIES
    if (client) {
      getKiosks({});
      getAdmins();
      getActivityLog({ page: 1 });
      setLogRefreshedAt(format(new Date(), 'MM/dd/yyyy hh:mm a'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  // ON PAGE CHANGE: FETCH MORE DATA
  React.useEffect(() => {
    if (page > 1) {
      let payload = {
        page,
        sort: sortDirection,
        sortColumn
      };
      if (filter) {
        const appliedFilters = getAppliedFilters();
        payload = { ...payload, ...appliedFilters };
      }
      getActivityLog(payload);
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ON EXPORT DATA FETCHED: WRITE TO CSV
  React.useEffect(() => {
    if (exportData && exportData.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formattedData = formatActivityLog(exportData).map((item: any) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const convertedItem: any = {};
        Object.entries(item).forEach(([key, value]) => {
          const upperCaseKey = key.toUpperCase();
          convertedItem[upperCaseKey] = value;
        });
        return convertedItem as BaseEvent;
      });

      const csvData = Papa.unparse(formattedData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, 'Activity Log.csv');

      resetExportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // INCREMENT PAGE TO TRIGGER NEXT FETCH
  const loadMore = () => {
    const activityLogTable: HTMLElement = document.getElementById('activityLogTable') || new HTMLElement();
    if (
      activityLogTable.offsetHeight < activityLogTable.scrollHeight &&
      activityLogTable.offsetHeight + activityLogTable.scrollTop >= activityLogTable.scrollHeight - 1
    ) {
      if (hasMore && !activityLogPending && !isLoading) {
        setIsLoading(true);
        setPage((prevPage) => prevPage + 1);
      }
    }
  };

  // RETURN ALL ACTIVE FILTERS FOR FETCH PAYLOAD
  const getAppliedFilters = () => {
    return {
      kioskIds: kioskIdsToFilter,
      startTime: selectedStartDate && moment(selectedStartDate).utc().format(),
      endTime: selectedEndDate && moment(selectedEndDate).utc().format(),
      userNames: usersToFilter
    };
  };

  // REFRESH BUTTON CLICK
  const onRefreshActivityLogClick = () => {
    resetActivityLog();
    setPage(1);
    getActivityLog({ page: 1 });
    setLogRefreshedAt(format(new Date(), 'MM/dd/yyyy hh:mm a'));
  };

  // EXPORT TO CSV BUTTON CLICK
  const onExportButtonClick = () => {
    let payload = {
      page: 1,
      sort: sortDirection,
      sortColumn,
      exportData: true
    };
    if (filter) {
      const appliedFilters = getAppliedFilters();
      payload = { ...payload, ...appliedFilters };
    }
    getActivityLog(payload);
  };

  // SET THE SORT COLUMN AND DIRECTION
  const handleSort = (column: string) => {
    let newSortDirection = 'asc';
    if (column === sortColumn) {
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      setSortColumn(column);
    }
    setSortDirection(newSortDirection);
    handleHeaderClick(column);
  };

  // SORT BY SELECTED HEADER AND FETCH
  const handleHeaderClick = (key: string) => {
    const sortOrder = sortParams.column === key && sortParams.order === 'asc' ? 'desc' : 'asc';
    setSortParams({ column: key, order: sortOrder });
    setPage(1);
    let payload = { sortColumn: key, sort: sortOrder, page: 1 };
    if (filter) {
      const appliedFilters = getAppliedFilters();
      payload = { ...payload, ...appliedFilters };
    }
    getActivityLog(payload);
  };

  // APPLY FILTER(S) AND FETCH
  const handleFilter = () => {
    setFilter(true);
    setPage(1);
    const appliedFilters = getAppliedFilters();
    getActivityLog({
      ...appliedFilters,
      page: 1,
      sort: sortDirection,
      sortColumn
    });
  };

  // CLEAR ALL ACTIVE FILTERS
  const resetFilters = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setKioskIdsToFilter([]);
    setUsersToFilter([]);
    setFilterChips([]);
  };

  // REMOVE AN ACTIVE FILTER
  const handleChipClose = (val: string) => {
    if (val === 'Kiosk Name') {
      setKioskIdsToFilter([]);
    } else if (val === 'User') {
      setUsersToFilter([]);
    } else if (val === 'Start Date Time') {
      setSelectedStartDate(null);
    } else if (val === 'End Date Time') {
      setSelectedEndDate(null);
    }

    const newChips = filterChips && filterChips.filter((chip: string) => chip !== val);
    setFilterChips(newChips);
  };

  const SortArrow = () => {
    return (
      <Box style={{ display: 'inline-block', cursor: 'pointer' }}>
        <Icon
          name={sortDirection === 'asc' ? 'drop_up' : 'drop_down'}
          color="#2A2A2A"
          size={15}
          style={{ marginLeft: '5px' }}
        />
      </Box>
    );
  };

  // IF NO CLIENT GO TO CLIENT SELECTION PAGE
  if (!client) return <Redirect to="/clients" />;

  return (
    <>
      {(activityLogPending || isLoading) && formattedActivityLog.length <= 0 && (
        <Overlay>
          <Loader color={color.LIGHT_GRAY} />
        </Overlay>
      )}

      {showConfirmation && !activityLogPending && (
        <ActionOverlay
          title={
            <MuiBox className={classes.activitylogNoDataBox}>
              <MuiTypography className={classes.activitylogNoDataHeader}>There is no data to display</MuiTypography>
            </MuiBox>
          }
          confirmText="Ok"
          onConfirm={() => setShowConfirmation(false)}
          height={340}
        />
      )}

      {/* HEADER ROW */}
      <Grid className={classes.activitylogHeading}>
        <Box flexOverride="none">
          <MuiTypography className={classes.activitylogText}>Activity Log</MuiTypography>
        </Box>
        <Grid className={classes.activityRefreshEventButton}>
          <Button
            size="small"
            onClick={onRefreshActivityLogClick}
            isLoading={activityLogPending && formattedActivityLog.length <= 0}
            data-testid="refresh-activity-log-button"
          >
            Refresh All Data
          </Button>
          <Typography tag="small" align="center" style={{ margin: '5px 0 0' }}>
            Last Refreshed: {logRefreshedAt}
          </Typography>
        </Grid>
      </Grid>

      <Grid
        className={classes.activitylogOptions}
        style={{ paddingBottom: filterChips && filterChips.length === 0 ? '24px' : '0px' }}
      >
        <Button size="small" onClick={() => setShowFiltersDrawer(true)} data-testid="filter-button">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Tune />
            <Typography style={{ margin: '0px 10px 0px 10px', fontSize: '16px' }}>Filters</Typography>
          </div>
        </Button>
        <Button
          onClick={() => onExportButtonClick()}
          isLoading={exportPending}
          style={{ width: '200px' }}
          isDisabled={formatActivityLog.length <= 0}
          data-testid="filter-button"
        >
          Export to .csv
        </Button>
      </Grid>
      {filterChips && filterChips.length > 0 && (
        <Grid className={classes.filterOptions}>
          {filterChips.map((chip: string, index: number) => (
            <Chip
              key={`${index}-${chip}`}
              style={{
                backgroundColor: '#F46D2E',
                color: 'white',
                fontWeight: 900,
                marginRight: '8px'
              }}
              className={classes.buttonStyl}
              label={chip}
              onDelete={() => {
                handleChipClose(chip);
                setChipClosed(true);
              }}
              variant="outlined"
            />
          ))}
        </Grid>
      )}

      {/* TABLE OF USERS */}
      <MuiBox className={classes.activitylogOuterBox}>
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <TableContainer
              className={classes.activitylogTableContainer}
              onScroll={() => loadMore()}
              id="activityLogTable"
            >
              <MuiTable stickyHeader style={{ borderCollapse: 'collapse' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      className={classes.activitylogHeaders}
                      style={{ fontWeight: 600 }}
                      onClick={() => handleSort('created_at')}
                    >
                      Date
                      {sortColumn === 'created_at' && <SortArrow />}
                    </TableCell>
                    <TableCell
                      className={classes.activitylogHeaders}
                      style={{ fontWeight: 600, width: '200px' }}
                      onClick={() => handleSort('actor_info')}
                    >
                      User
                      {sortColumn === 'actor_info' && <SortArrow />}
                    </TableCell>
                    <TableCell
                      className={classes.activitylogHeaders}
                      style={{ fontWeight: 600, width: '180px' }}
                      onClick={() => handleSort('name')}
                    >
                      Kiosk Name
                      {sortColumn === 'name' && <SortArrow />}
                    </TableCell>
                    <TableCell
                      className={classes.activitylogHeaders}
                      style={{ fontWeight: 600 }}
                      onClick={() => handleSort('event')}
                    >
                      Action
                      {sortColumn === 'event' && <SortArrow />}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    formattedActivityLog.map((activitylogObj: any) => {
                      return (
                        <TableRow className={classes.activitylogTableRows}>
                          <TableCell>
                            <Typography size={16} weight={600}>
                              {activitylogObj.date}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography size={16}>{activitylogObj.user}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography size={16}>{activitylogObj.kioskName}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography>{activitylogObj.action}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  }
                </TableBody>
              </MuiTable>
            </TableContainer>
          </Box>
        </Card>
      </MuiBox>
      <Grid className={classes.footer}>
        <p className={classes.total}>Actions: {totalRecords}</p>
      </Grid>
      <ActivityLogFilterPopUp
        isOpen={showFiltersDrawer}
        onClose={() => setShowFiltersDrawer(false)}
        kioskIdsToFilter={kioskIdsToFilter}
        setKioskIdsToFilter={setKioskIdsToFilter}
        usersToFilter={usersToFilter}
        setUsersToFilter={setUsersToFilter}
        selectedStartDate={selectedStartDate}
        setSelectedStartDate={setSelectedStartDate}
        selectedEndDate={selectedEndDate}
        setSelectedEndDate={setSelectedEndDate}
        filterChips={filterChips}
        setFilterChips={setFilterChips}
        kioskList={kioskData}
        admins={admins}
        handleFilter={handleFilter}
      />
    </>
  );
};

export default ActivityLogPage;
